import { useState } from 'react'
import { Button } from '../../ui/Button'
import type { DealDetail } from '../../api/adapters/dealDetail'
import type { ParticipantReviewState } from '../../api/types'
import { LeadDetailsForm } from './LeadDetailsForm'
import { PeopleList } from './PeopleList'
import styles from './DealPanel.module.css'

/**
 * The lead: who the deal is with, and who was in the room.
 *
 * The review prompt is computed by the backend (`participant_review`) and simply
 * displayed. The previous frontend decided this itself and got the most
 * important case wrong: with nobody identified it raised no prompt at all, while
 * the deal silently could not reach Keep.
 */
const MESSAGE: Record<Exclude<ParticipantReviewState, 'none'>, string> = {
  unidentified:
    'No one on the client side could be identified from these calls. This happens when they join from a meeting room or a shared account. Until someone is confirmed as the decision maker, this deal cannot be recommended as Keep.',
  unconfirmed:
    'Some people detected on these calls have not been reviewed yet. Confirming who decides is what lets this deal reach Keep.',
}

export function LeadDetails({ deal }: { deal: DealDetail }) {
  const [editing, setEditing] = useState(false)

  return (
    <section>
      <h3 className={styles.section}>Lead details</h3>

      {/* Keyed off the MESSAGE lookup, not off `!== 'none'`. A backend that does
          not send `participant_review` at all leaves it undefined, which is not
          'none' — so the old test rendered the banner with `MESSAGE[undefined]`
          inside it: an empty blue box on every deal. That is exactly what
          happened when v2 was served against a backend without the field. A
          banner with nothing to say must not appear. */}
      {MESSAGE[deal.participantReview as keyof typeof MESSAGE] ? (
        <p className={styles.review} role="status">
          {MESSAGE[deal.participantReview as keyof typeof MESSAGE]}
        </p>
      ) : null}

      {editing ? (
        <LeadDetailsForm deal={deal} onDone={() => setEditing(false)} />
      ) : (
        <>
          <dl className={styles.facts}>
            <dt>Location</dt>
            <dd>{deal.country ?? 'Not set'}</dd>
            <dt>Email</dt>
            <dd>{deal.contactEmail ?? 'Not set'}</dd>
          </dl>
          <PeopleList participants={deal.participants} />
          <div className={styles.editActions}>
            <Button onClick={() => setEditing(true)}>Edit lead details</Button>
          </div>
        </>
      )}
    </section>
  )
}
